import React, { useMemo, useCallback, useState, useEffect } from "react"
import { Formik, Form as FormikForm } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { Label, Button, FormFeedback, Row, Col, Card, CardBody } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"

import { useCreateAccessoriesUsage, useEditAccessoriesUsage } from "hooks/useAccessoriesUsage"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"

const AccessoriesUsageAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  // -------------------- Fetch dropdown options --------------------
  const { raw: rawOptions, isLoading: optionLoading } = useFilteredVehicleOptions()

  // -------------------- State for created accessories --------------------
  const [createdAccessories, setCreatedAccessories] = useState([])

  useEffect(() => {
    if (!modal) setCreatedAccessories([])
  }, [modal])

  // -------------------- Combined options --------------------
  const employeeOptions = useMemo(
    () => (rawOptions?.employees ?? []).map(e => ({ value: e.id?.toString(), label: e.name })),
    [rawOptions?.employees]
  )

  const accessoryOptions = useMemo(() => {
    const rawAccessories = rawOptions?.accessories_lists ?? []
    const combined = [...rawAccessories, ...createdAccessories]
    return combined.map(a => ({ value: a.id?.toString(), label: a.name }))
  }, [rawOptions?.accessories_lists, createdAccessories])

  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccessoriesUsage()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccessoriesUsage()

  // -------------------- Form Initial Values --------------------
  const initialValues = useMemo(() => {
    const initialItems = editedData?.items?.length
      ? editedData.items.map(item => ({ accessory_id: item.accessory_id?.toString() || "", quantity: item.quantity || "" }))
      : [{ accessory_id: "", quantity: "" }]

    return {
      usage_date: editedData?.usage_date || "",
      usage_by_id: editedData?.usage_by_id?.toString() || "",
      vehicle_id: "", // VehicleIdSelect will handle setting the object
      items: initialItems,
    }
  }, [editedData])

  // -------------------- Validation Schema --------------------
  const validationSchema = Yup.object({
    usage_date: Yup.date().required("Please select usage date"),
    usage_by_id: Yup.string().required("Please select an employee"),
    vehicle_id: Yup.mixed().required("Please select a vehicle"),
    items: Yup.array()
      .of(
        Yup.object({
          accessory_id: Yup.string().required("Select an accessory"),
          quantity: Yup.number().required("Enter quantity"),
        })
      )
      .min(1, "Add at least one accessory"),
  })

  // -------------------- Handle Option Creation --------------------
  const handleCreateOption = useCallback((inputValue) => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedAccessories(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  // -------------------- Submit Handler --------------------
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          vehicle_id: values.vehicle_id?.value || values.vehicle_id,
          items: values.items.map(item => ({
            ...item,
            quantity: Number(item.quantity),
            accessory_id: item.accessory_id?.startsWith("new_") ? item.accessory_id.split("_")[1] : item.accessory_id,
          })),
        }

        const action = isEdit ? editMutation({ id: editedData.id, ...payload }) : createMutation(payload)
        const res = await action
        toast.success(res?.message ?? `Accessories Usage ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save accessories usage.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const buttonText = isEdit ? "Save Changes" : "Add Usage"
  const isSubmitDisabled = editLoading || createLoading

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessories Usage" : "Add Accessories Usage"}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize={isEdit}>
        {formik => {
          // Set vehicle_id when editing
          useEffect(() => {
            if (editedData?.vehicle?.id) {
              formik.setFieldValue("vehicle_id", {
                label: editedData.vehicle.stock_id + " - " + (editedData.vehicle.full_chassis || ""),
                value: editedData.vehicle.id,
              })
            } else {
              formik.setFieldValue("vehicle_id", null)
            }
          }, [editedData])

          const addItem = () => formik.setFieldValue("items", [...formik.values.items, { accessory_id: "", quantity: "" }])
          const removeItem = index => {
            const newItems = formik.values.items.filter((_, i) => i !== index)
            formik.setFieldValue("items", newItems.length ? newItems : [{ accessory_id: "", quantity: "" }])
          }

          return (
            <FormikForm>
              {/* Usage Date */}
              <div className="mb-3">
                <Label htmlFor="usage_date">Usage Date <span className="text-danger">*</span></Label>
                <InputField
                  type="datetime-local"
                  name="usage_date"
                  id="usage_date"
                  value={formik.values.usage_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.usage_date && !!formik.errors.usage_date}
                />
                {formik.touched.usage_date && formik.errors.usage_date && <FormFeedback type="invalid">{formik.errors.usage_date}</FormFeedback>}
              </div>

              {/* Employee */}
              <FormikCreatableSelect
                name="usage_by_id"
                label="Used By"
                formik={formik}
                options={employeeOptions}
                placeholder="Select employee"
                loading={optionLoading}
                required
              />

              {/* Vehicle */}
              <VehicleIdSelect formik={formik} />

              {/* Accessories Items */}
              {formik.values.items.map((item, index) => (
                <Card key={index} className="mb-3 mt-3 p-3 border">
                  <CardBody>
                    <h6>Accessory #{index + 1}</h6>

                    <FormikCreatableSelect
                      name={`items.${index}.accessory_id`}
                      label="Accessory"
                      formik={formik}
                      options={accessoryOptions}
                      placeholder="Select accessory"
                      loading={optionLoading}
                      required
                      onCreateOption={handleCreateOption}
                    />

                    <Row className="mt-2">
                      <Col md={6}>
                        <Label>Quantity <span className="text-danger">*</span></Label>
                        <InputField
                          type="number"
                          name={`items.${index}.quantity`}
                          value={item.quantity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.items?.[index]?.quantity && formik.errors.items?.[index]?.quantity && (
                          <FormFeedback type="invalid" className="d-block">{formik.errors.items[index].quantity}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    <Button color="danger" size="sm" className="mt-2" onClick={() => removeItem(index)} type="button">
                      Remove
                    </Button>
                  </CardBody>
                </Card>
              ))}

              <Button color="primary" type="button" onClick={addItem} className="mb-3">Add Another Accessory</Button>

              {/* Submit */}
              <div className="text-end mt-3">
                <Button color="success" type="submit" disabled={isSubmitDisabled}>{buttonText}</Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </ThemeModal>
  )
}

export default AccessoriesUsageAddEdit
